import { ReactNode } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import { formatAddress, formatDate } from 'dashboard/lib/utils/formatters';
import {
  FinraBrokerCheckBrokerStatus,
  FinraBrokerCheckBrokerType,
  FinraBrokerCheckResultFirm,
  FinraBrokerCheckResultIndividual,
  ReportFinraBrokerCheck,
} from 'dashboard/models/report/finra-broker-check';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import joinWithNode from 'lib/utils/joinWithNode';

const BrokerHeader = styled('div')`
  display: flex;
  flex-direction: column;
`;

const BrokerHeaderTitle = styled('div')`
  display: flex;
  align-items: center;
`;

const BrokerStatusSticker = ({ status }: { status?: FinraBrokerCheckBrokerStatus }) => {
  switch (status) {
    case FinraBrokerCheckBrokerStatus.Active:
      return <Sticker variant='success'>Active</Sticker>;
    case FinraBrokerCheckBrokerStatus.NotInScope:
      return <Sticker variant='caution'>Not in scope</Sticker>;
    case FinraBrokerCheckBrokerStatus.Inactive:
      return <Sticker variant='danger'>Inactive</Sticker>;
    default:
      return <Sticker variant='basic'>Unknown</Sticker>;
  }
};

interface FinraBrokerCheckProps {
  report: ReportFinraBrokerCheck;
  isRedacted: boolean;
}

const FinraBrokerCheck = ({ report }: FinraBrokerCheckProps) => {
  const { result: result } = report.attributes;
  if (result == null) {
    return null;
  }

  const brokerAccordions = result.map((finraBrokerCheckResult, matchIdx): ReactNode => {
    switch (report.attributes['broker-type']) {
      case FinraBrokerCheckBrokerType.Individual: {
        const finraBrokerCheckResultIndividual: FinraBrokerCheckResultIndividual =
          finraBrokerCheckResult;
        return (
          <Accordion
            titleContainerHeight='auto'
            title={
              <BrokerHeader>
                <BrokerHeaderTitle>
                  <p>
                    {[
                      finraBrokerCheckResultIndividual['name-first'],
                      finraBrokerCheckResultIndividual['name-last'],
                    ].join(' ')}
                  </p>
                  <Spacer width={2} />
                  <BrokerStatusSticker status={finraBrokerCheckResultIndividual['broker-status']} />
                  <Spacer width={2} />
                  {finraBrokerCheckResultIndividual['has-disclosure-filings'] && (
                    <Sticker variant='danger'>Disclosures</Sticker>
                  )}
                </BrokerHeaderTitle>
              </BrokerHeader>
            }
            menuItem={true}
            padded={false}
          >
            <Spacer height={3} />
            <AttributeGrid key={matchIdx}>
              <AttributeItem
                title='CRD#'
                value={
                  <a
                    href={`https://brokercheck.finra.org/individual/summary/${finraBrokerCheckResultIndividual['crd-number']}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {finraBrokerCheckResultIndividual['crd-number']}
                  </a>
                }
                placeholder='N/A'
                dataTest='report-finra-broker-check__name-last'
              />
              <AttributeItem
                title='Other names'
                value={joinWithNode(null, finraBrokerCheckResultIndividual['other-names'] ?? [])}
                placeholder='N/A'
                dataTest='report-finra-broker-check__other-names'
              />
              <AttributeItem
                title='Broker'
                value={
                  <BrokerStatusSticker status={finraBrokerCheckResultIndividual['broker-status']} />
                }
                placeholder='N/A'
                dataTest='report-finra-broker-check__broker-status'
              />
              <AttributeItem
                title='Investment advisor'
                value={
                  <BrokerStatusSticker
                    status={finraBrokerCheckResultIndividual['investment-advisor-status']}
                  />
                }
                placeholder='N/A'
                dataTest='report-finra-broker-check__investment-advisor-status'
              />
              <AttributeItem
                title='Disclosures'
                value={
                  finraBrokerCheckResultIndividual['has-disclosure-filings'] ? (
                    <Sticker variant='danger'>Yes</Sticker>
                  ) : (
                    <Sticker variant='success'>No</Sticker>
                  )
                }
                placeholder='N/A'
                dataTest='report-finra-broker-check__has-disclosure-filings'
                helpMessage={
                  'All individuals registered to sell securities or provide investment advice are required to disclose customer' +
                  ' complaints and arbitrations, regulatory actions, employment terminations, bankruptcy filings, and criminal' +
                  ' or civil judicial proceedings.'
                }
              />
              <AttributeItem
                title='Start date'
                value={formatDate(finraBrokerCheckResultIndividual['start-date'])}
                placeholder='N/A'
                dataTest='report-finra-broker-check__start-date'
              />
              <AttributeItem
                title='Approved registrations'
                value={finraBrokerCheckResultIndividual['approved-finra-registration-count']}
                placeholder='N/A'
                dataTest='report-finra-broker-check__approved-finra-registration-count'
              />
              <AttributeItem
                title='Employments'
                value={finraBrokerCheckResultIndividual['employments-count']}
                placeholder='N/A'
                dataTest='report-finra-broker-check__employments-count'
              />
            </AttributeGrid>
            <Spacer height={3} />
          </Accordion>
        );
      }
      case FinraBrokerCheckBrokerType.Firm: {
        const finraBrokerCheckResultFirm: FinraBrokerCheckResultFirm = finraBrokerCheckResult;
        const address = formatAddress({
          addressStreet1: finraBrokerCheckResultFirm['office-address']?.['street-1'],
          addressStreet2: finraBrokerCheckResultFirm['office-address']?.['street-2'],
          addressCity: finraBrokerCheckResultFirm['office-address']?.['city'],
          addressSubdivision: finraBrokerCheckResultFirm['office-address']?.['subdivision'],
          addressPostalCode: finraBrokerCheckResultFirm['office-address']?.['postal-code'],
        });
        return (
          <Accordion
            titleContainerHeight='auto'
            title={
              <BrokerHeader>
                <BrokerHeaderTitle>
                  <p>{finraBrokerCheckResultFirm.name}</p>
                  <Spacer width={2} />
                  <BrokerStatusSticker status={finraBrokerCheckResultFirm.status} />
                  <Spacer width={2} />
                  {finraBrokerCheckResultFirm['has-disclosure-filings'] && (
                    <Sticker variant='danger'>Disclosures</Sticker>
                  )}
                </BrokerHeaderTitle>
                {address && (
                  <>
                    <Paragraph size='md' color='tertiary'>
                      {address}
                    </Paragraph>
                    <Spacer height={1} />
                  </>
                )}
              </BrokerHeader>
            }
            menuItem={true}
            padded={false}
          >
            <Spacer height={3} />
            <AttributeGrid key={matchIdx}>
              <AttributeItem
                title='CRD#'
                value={
                  <a
                    href={`https://brokercheck.finra.org/firm/summary/${finraBrokerCheckResultFirm['crd-number']}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {finraBrokerCheckResultFirm['crd-number']}
                  </a>
                }
                placeholder='N/A'
                dataTest='report-finra-broker-check__crd-number'
              />
              <AttributeItem
                title='SEC#'
                value={finraBrokerCheckResultFirm['broker-dealer-full-sec-number']}
                placeholder='N/A'
                dataTest='report-finra-broker-check__other-names'
              />
              <AttributeItem
                title='Approved registrations'
                value={finraBrokerCheckResultFirm['approved-finra-registration-count']}
                placeholder='N/A'
                dataTest='report-finra-broker-check__approved-finra-registration-count'
              />
              <AttributeItem
                title='Mailing address'
                value={formatAddress({
                  addressStreet1: finraBrokerCheckResultFirm['mailing-address']?.['street-1'],
                  addressStreet2: finraBrokerCheckResultFirm['mailing-address']?.['street-2'],
                  addressCity: finraBrokerCheckResultFirm['mailing-address']?.['city'],
                  addressSubdivision:
                    finraBrokerCheckResultFirm['mailing-address']?.['subdivision'],
                  addressPostalCode: finraBrokerCheckResultFirm['mailing-address']?.['postal-code'],
                })}
                placeholder='N/A'
                dataTest='report-finra-broker-check__mailing-address'
              />
              <AttributeItem
                title='Phone number'
                value={finraBrokerCheckResultFirm['phone-number']}
                placeholder='N/A'
                dataTest='report-finra-broker-check__phone-number'
              />
            </AttributeGrid>
            <Spacer height={3} />
          </Accordion>
        );
      }
      default:
        return null;
    }
  });

  return <>{brokerAccordions}</>;
};

export default FinraBrokerCheck;
